import { EntityLink as Link } from '../../navigation/navigation'
import { useListState } from '../../navigation/useListState'
import React, { useState, useEffect } from 'react'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { Input, Select, Button, Pagination, TagBadge } from '../../components/ui'
import { getClientTypeTagVariant } from '../../utils/clientTypeTag'
import './Company.css'

function Clients() {
  const { filters, setFilters, pagination, setPagination } = useListState({
    filters: {
      type: '',
      search: ''
    },
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 1
    },
  })
  const navigate = useNavigate()
  const { user } = useAuth()
  const canCreate = user?.permissions?.['clients.create']
  const [clients, setClients] = useState([])
  const [clientTypes, setClientTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)

  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await api.get('/company/clients/types')
        if (response.data?.types) setClientTypes(response.data.types)
      } catch (e) {
        console.error('Error fetching client types:', e)
      }
    }
    fetchTypes()
  }, [])

  const effectiveSearch = (filters.search || '').trim().length >= 3 ? (filters.search || '').trim() : ''
  useEffect(() => {
    fetchClients()
  }, [filters.type, pagination.page, effectiveSearch])

  const fetchSuggestions = React.useCallback(async (q) => {
    const query = (q || '').trim()
    if (query.length < 3) {
      setSuggestions([])
      return
    }
    try {
      const res = await api.get(`/company/clients/suggest?q=${encodeURIComponent(query)}&limit=10`)
      setSuggestions(res.data?.suggestions || [])
    } catch {
      setSuggestions([])
    }
  }, [])

  useEffect(() => {
    if (!filters.search || filters.search.length < 3) {
      setSuggestions([])
      return
    }
    const t = setTimeout(() => fetchSuggestions(filters.search), 300)
    return () => clearTimeout(t)
  }, [filters.search, fetchSuggestions])

  const applySuggestion = (client) => {
    handleFilterChange('search', client.name || '')
    setSuggestions([])
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchClients = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.type) params.append('type', filters.type)
      if (filters.search && filters.search.trim().length >= 3) params.append('search', filters.search.trim())
      params.append('page', pagination.page.toString())
      params.append('limit', pagination.limit.toString())

      const response = await api.get(`/company/clients?${params}`)
      
      if (response.data) {
        if (response.data.clients) {
          setClients(response.data.clients)
          setPagination(prev => ({
            ...prev,
            total: response.data.total || 0,
            pages: response.data.pages || 1
          }))
        } else {
          setClients([])
        }
      } else {
        setClients([])
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
      console.error('Error details:', error.response?.data || error.message)
      setClients([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    const hadEffectiveSearch = (filters.search || '').trim().length >= 3
    const hasEffectiveSearch = (value || '').trim().length >= 3
    const resetPage = field !== 'search' || hadEffectiveSearch !== hasEffectiveSearch
    setFilters(prev => ({ ...prev, [field]: value }))
    if (resetPage) setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const formatAmount = (amount) => {
    if (amount == null || amount === '') return '-'
    const num = typeof amount === 'number' ? amount : parseFloat(amount)
    if (isNaN(num)) return '-'
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(num)
  }

  const getActsAmountHighlightClass = (contractsTotal, actsAmount) => {
    const c = typeof contractsTotal === 'number' ? contractsTotal : parseFloat(contractsTotal)
    const a = typeof actsAmount === 'number' ? actsAmount : parseFloat(actsAmount)
    if (c === 0 && a === 0) return ''
    if (c > 0 && a === c) return 'amount-acts-equal'
    if (c > 0 && a < c) return 'amount-acts-less'
    return ''
  }

  usePageHeader({
    renderActions: () => canCreate ? (
      <Button variant="primary" onClick={() => navigate('/company/clients/new')}>+ Создать клиента</Button>
    ) : null
  }, [canCreate])

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="company-page">
      <div className="filters-section">
        <div className="filters">
          <div className="filter-group">
            <label>Тип клиента</label>
            <Select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              aria-label="Тип клиента"
            >
              <option value="">Все типы</option>
              {clientTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group filter-group-search">
            <label>Поиск</label>
            <Input
              type="text"
              placeholder="Поиск по названию или ИНН"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onFocus={() => filters.search && filters.search.trim().length >= 3 && fetchSuggestions(filters.search)}
              onBlur={() => setTimeout(() => setSuggestions([]), 200)}
              aria-label="Поиск по названию или ИНН"
              autoComplete="off"
            />
            {suggestions.length > 0 && (
              <ul className="search-suggestions" role="listbox">
                {suggestions.map((s) => (
                  <li
                    key={s.id}
                    role="option"
                    className="search-suggestion-item"
                    onMouseDown={() => applySuggestion(s)}
                  >
                    <span className="search-suggestion-name">{s.name || '-'}</span>
                    {s.inn && <span className="search-suggestion-inn">ИНН {s.inn}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="table-section">
        {clients.length === 0 ? (
          <div className="no-data">Клиенты не найдены</div>
        ) : (
          <>
            {isMobile ? (
              <>
                <div className="list-cards">
                  {clients.map((client) => (
                    <div
                      key={client.id}
                      className="list-card ui-card ui-card-hover"
                      onClick={() => navigate(`/client/${client.id}`)}
                    >
                      <div className="list-card-header">
                        <Link to={`/client/${client.id}`} className="list-card-title">{client.name || '-'}</Link>
                      </div>
                      <div className="list-card-body">
                        <div className="list-card-row">
                          <span className="list-card-label">Тип:</span>
                          <span className="list-card-value">
                            {client.type ? (
                              <TagBadge variant={getClientTypeTagVariant(client.type)}>{client.type}</TagBadge>
                            ) : (
                              '-'
                            )}
                          </span>
                        </div>
                        <div className="list-card-row">
                          <span className="list-card-label">Объектов:</span>
                          <span className="list-card-value mono">{client.objects_count ?? '-'}</span>
                        </div>
                        <div className="list-card-row">
                          <span className="list-card-label">Договоров на сумму:</span>
                          <span className="list-card-value mono">{formatAmount(client.contracts_total_amount)}</span>
                        </div>
                        <div className="list-card-row">
                          <span className="list-card-label">Сумма актов:</span>
                          <span
                            className={`list-card-value mono ${getActsAmountHighlightClass(client.contracts_total_amount, client.acts_amount)}`}
                          >
                            {formatAmount(client.acts_amount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {pagination.total > 0 && (
                  <div className="company-pagination-bar-wrap">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      pageSize={pagination.limit}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="company-table-frame">
                <div className="table-responsive">
                  <table className="ui-table data-table">
                    <thead>
                      <tr>
                        <th>Название</th>
                        <th>Тип</th>
                        <th className="text-right">Объектов</th>
                        <th className="text-right">Договоров на сумму</th>
                        <th className="text-right">Актов (сумма)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr
                          key={client.id}
                          className="clickable-row"
                          onClick={() => navigate(`/client/${client.id}`)}
                        >
                          <td>
                            <Link to={`/client/${client.id}`} className="list-primary-name">{client.name || '-'}</Link>
                          </td>
                          <td>
                            {client.type ? (
                              <TagBadge variant={getClientTypeTagVariant(client.type)}>{client.type}</TagBadge>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="text-right mono">{client.objects_count || 0}</td>
                          <td className="text-right mono">
                            {formatAmount(client.contracts_total_amount)}
                          </td>
                          <td className="text-right mono">
                            <span className={getActsAmountHighlightClass(client.contracts_total_amount, client.acts_amount)}>
                              {formatAmount(client.acts_amount)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {pagination.total > 0 && (
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Clients

